<template>
    <b-modal :id="modalId" :title="title" size="lg" hide-footer @show="show">
        <b-alert v-model="showInvalidFormAlert" variant="danger">
            <span v-if="prescriber && !prescriber.faxNumber">
                Prescriber doesn't have any fax number.
            </span>
        </b-alert>
        <b-row>
            <b-col cols="6" v-if="formType != formTypeEnum.NewRxRequest">
                <b>FROM: </b><span>{{ store.name }}</span><br>
                <span>{{ store.fullAddress }}</span><br>
            </b-col>

            <b-col cols="12" v-else>
                <SearchComponent v-model="storeFilter" label="Store" search-u-r-l="/Store" :params="{active: true}" prepopulate />
            </b-col>
            <b-col cols="12">
                <SearchComponent v-if="formType == formTypeEnum.NewRxRequest" v-model="selectedPrescriber"
                    label="Doctor" search-u-r-l="/Prescriber" />
            </b-col>
        </b-row>
        <hr>
        <b-row>
            <b-col cols="6">
                <b>PATIENT: </b><span>{{ patient.displayNamesForPerson() }}</span><br>
                <span>{{ patient.fullAddress }}</span><br>
                <b>PATIENT DOB: </b><span>{{ patient.dateOfBirth | formatDate('L', false) }}</span>
            </b-col>
        </b-row>
        <b-row>
            <b-col cols="4">
                <b>PHONE: </b><span>{{ patient.phoneNumber }}</span><br>
            </b-col>
            <b-col cols="4">
                <b>CELL PHONE: </b>{{ patient.cellNumber }}<br>
            </b-col>
        </b-row>
        <hr>
        <b-row>
            <b-col cols="12">
                <SearchComponent v-if="formType == formTypeEnum.NewRxRequest" v-model="selectedDrug" label="Drug"
                    search-u-r-l="/Drug" />
                <b v-else>PRESCRIBED: </b><span>{{ drugToShow.productNameLong }}</span>
            </b-col>
        </b-row>

        <PrescriptionDetailsSummary v-if="prescription && formType != formTypeEnum.NewRxRequest"
            v-model="prescription" />
        <hr>

        <b-row>
            <b-col>
                <fg-input label="PHARMACY COMMENTS">
                    <b-form-textarea v-model="faxFormData.note" block-listener placeholder="Write here any comments" />
                </fg-input>
            </b-col>
        </b-row>

        <b-row v-if="formType === formTypeEnum.RxClarification">
            <b-col>
                <b-check v-model="appendPrescriptionImage">
                    Append prescription image to fax
                </b-check>
            </b-col>
        </b-row>

        <b-row v-if="prescriberToShow && prescriberToShow.altFaxNumber">
            <b-col>
                <b-check v-model="useAltFax">
                    Use Alternate Fax Number ({{ prescriberToShow.altFaxNumber }})
                </b-check>
            </b-col>
        </b-row>

        <hr>

        <b-row v-if="isSending">
            <b-spinner label="Spinning" class="mr-2" />
            <strong>Sending...</strong>
        </b-row>
        <b-row v-else>
            <b-col>
                <b-button class="mt-3" block @click="close">
                    Cancel
                </b-button>
            </b-col>
            <b-col>
                <b-button class="mt-3" variant="success" block @click="send" :disabled="!isFormValid">
                    Send
                </b-button>
            </b-col>
        </b-row>

    </b-modal>
</template>

<script lang="ts">
import { Vue, Component, Prop } from "vue-property-decorator";
import { Store } from '@/models/Store';
import { Prescriber } from '@/models/Prescriber';
import { Patient } from '@/models/Patient';
import { FaxForm, Prescription } from '@/models/Prescription';
import { PatientDocument } from "@/models/PatientDocument";
import { FaxType } from "@/models/PrescriberFollowUpCase";
import { Drug } from "@/models/Drug/Drug";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import PrescriptionDetailsSummary from '@/components/Prescription/PrescriptionDetailsSummary.vue'
import SearchComponent from '@/components/SearchComponent.vue';
import { PrescriberContactTemplate } from "@/models/FaxTemplates";

@Component({
    name: "FaxToPhysicianFormModal",
    components: { PrescriptionDetailsSummary, SearchComponent }
})
export default class FaxToPhysicianFormModal extends Vue {
    @Prop({ default: () => new Store() }) private store!: Store;
    @Prop({ default: () => new Prescriber() }) private prescriber!: Prescriber;
    @Prop() private prescription!: Prescription;
    @Prop() private patient!: Patient;
    @Prop({ default: () => new Drug() }) private drug!: Drug;
    @Prop({ default: "" }) private modalId!: string;
    @Prop({ default: FaxType.RefillAuthorization }) private formType!: FaxType;
    @Prop({ default: null }) private customTemplate!: PrescriberContactTemplate | null;

    private faxFormData: FaxForm = new FaxForm();
    private isSending: boolean = false;

    private selectedPrescriber: Prescriber = new Prescriber();
    private selectedDrug: Drug = new Drug();

    protected formTypeEnum = FaxType;

    private storeFilter: Store = new Store();
    private excludeRefillRequestsFromNotes: boolean = false;

    protected appendPrescriptionImage = false;
    protected useAltFax = false;

    created() {
        this.getConfig();
    }

    get lastStore(): any {
        const objStr = localStorage.getItem('printQueue_lastStore');
        if (objStr) return JSON.parse(objStr);
        else return null;
    }

    get title(): string {
        if (this.customTemplate) return this.customTemplate.name;

        if (this.formType == FaxType.RefillAuthorization) return 'Refill Authorization Request';
        if (this.formType == FaxType.RxClarification) return 'RX Clarification Request';
        if (this.formType == FaxType.NewRxRequest) return 'New RX Request';
        if (this.formType == FaxType.AlternateRxRequest) return 'Alternate RX Request';

        return '';
    }

    get endpointURI(): string {
        if (this.customTemplate) return `custom-prescriber-fax-send/${this.customTemplate.id}`;

        if (this.formType == FaxType.RefillAuthorization) return 'request-refill-authorization';
        if (this.formType == FaxType.RxClarification) return 'request-rx-clarification';
        if (this.formType == FaxType.NewRxRequest) return 'request-new-rx';
        return "";
    }

    get prescriberToShow(): Prescriber {
        return this.prescriber?.id ? this.prescriber : this.selectedPrescriber;
    }

    get drugToShow(): Drug {
        return this.drug?.packageID ? this.drug : this.selectedDrug;
    }

    show() {
        this.clearForm();
    }

    async send() {
        this.faxFormData.fax = this.useAltFax && this.prescriberToShow.altFaxNumber ?
            this.prescriberToShow.altFaxNumber : this.prescriberToShow.faxNumber;

        if (this.faxFormData.verbalTransfer == null) {
            this.faxFormData.verbalTransfer = false;
        }

        if (this.formType == FaxType.NewRxRequest) {
            this.faxFormData.prescriberID = Number(this.selectedPrescriber.id);
            this.faxFormData.patientID = Number(this.patient.id);
            this.faxFormData.packageID = Number(this.selectedDrug.drugId);
            this.faxFormData.drugSource = Number(this.selectedDrug.source);
            this.faxFormData.storeID = Number(this.storeFilter.id);
        } else {
            this.faxFormData.rxID = this.prescription.rxID;
            this.faxFormData.storeID = Number(this.store.id);
            this.faxFormData.appendPrescriptionImage = this.appendPrescriptionImage;
            this.faxFormData.excludeRefillRequestsFromNotes = this.excludeRefillRequestsFromNotes;
        }

        try {
            this.isSending = true;

            const res = await this.$http.post<PatientDocument>(`prescription/${this.endpointURI}`, this.faxFormData);

            if (!res.data.id) return;

            let msg = `${this.title} sent successfully and saved as a new patient document. A new Follow-Up case was created.`;

            const canSendClarificationFax = this.$permissions.some(p => p.permissionCodeName.toLowerCase() === "Pharmacists/Fax Rx Clarification Request".toLowerCase());

            if (!canSendClarificationFax && this.formType == FaxType.RxClarification) {
                msg = `${this.title} was saved as a new patient document and new Follow-Up case was created for review.`;
            }

            if (this.customTemplate) {
                msg = `${this.customTemplate.name} sent successfully and saved as a new patient document.`;
            }

            this.$notification(
                NotificationOptions.notificationOptionsPreset(msg, NotificationOptions.NotificationTypes.success)
            );
            this.clearForm();
            this.$emit('saved');
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            const errorMsg = `Error while sending ${this.title}.`;
            console.error(errorMsg, err);
        } finally {
            this.isSending = false;
        }
    }

    async getConfig() {
        try {
            this.excludeRefillRequestsFromNotes = (await this.$http.get<boolean>("/configuration/ExcludeRefillRequestsFromNotes")).data;
        } catch (error) {
            console.error("Failed to load configuration:", error);
            this.excludeRefillRequestsFromNotes = false; // Safe default
        }
    }

    close() {
        this.$bvModal.hide(this.modalId);
        this.selectedDrug = new Drug();
        this.selectedPrescriber = new Prescriber();
    }

    clearForm() {
        this.faxFormData = new FaxForm();
    }

    get isFormValid() {
        const validSelectedFields = this.formType == FaxType.NewRxRequest && !!this.selectedPrescriber.id && !!this.selectedDrug.drugId
            || this.formType != FaxType.NewRxRequest;

        return !!this.prescriberToShow.faxNumber && validSelectedFields;
    }

    get showInvalidFormAlert() {
        return !this.isFormValid && this.formType != FaxType.NewRxRequest;
    }

    mounted() {
        this.storeFilter = new Store(this.lastStore) ?? new Store();
    }
}
</script>

<style scoped></style>
